import { useEffect, useMemo, useState } from "react";
import { TopNav } from "../components/TopNav";
import { Footer } from "../components/Footer";
import styles from "./StickiesPage.module.css";

/**
 * Stickies — per-app marketing page. Same chrome as the homepage
 * (TopNav + Footer) so users can jump back to /, plus six sections
 * tuned to the app:
 *
 *   1. Hero — display-type wordmark + scattered animated stickies.
 *   2. DesktopLayer — explains the "lives on your desktop" model.
 *   3. Palette — the seven note colors as swatches.
 *   4. Schedule — recurring reminders, with a faithful dialog mock.
 *   5. Features — six-card ribbon on the dark ink ground.
 *   6. CTA — "Comes with Atlas Desk" close-out.
 *
 * Reuses the shared design tokens (var(--accent-3), atlasContainer,
 * atlasEyebrow). No Tailwind, no per-page React Router.
 */
export function StickiesPage() {
  return (
    <>
      <TopNav />
      <main>
        <Hero />
        <DesktopLayer />
        <Palette />
        <Schedule />
        <Features />
        <Cta />
      </main>
      <Footer />
    </>
  );
}

/* ---------- Hero ---------- */

function Hero() {
  return (
    <section className={styles.hero} aria-label="Stickies">
      <div className={styles.heroWallpaper} aria-hidden />
      <div className={styles.heroGrain} aria-hidden />

      <div className={`atlasContainer ${styles.heroInner}`}>
        <div className={styles.heroCopy}>
          <span className={styles.heroEyebrow}>ATLAS</span>
          <h1 className={styles.heroTitle}>STICKIES</h1>
          <p className={styles.heroLede}>
            Notes that live on your desktop.
            <br />
            Sticks to your screen so you <br/>can stick to your day.
          </p>
          <div className={styles.heroActions}>
            <a className={styles.heroPrimary} href="/#download">
              Download <span aria-hidden>→</span>
            </a>
            <a
              className={styles.heroSecondary}
              href="/preview/"
              target="_blank"
              rel="noopener"
              title="Open the live Atlas Desk preview in a new tab"
            >
              <span className={styles.heroSecondaryDot} aria-hidden />
              Try in browser
            </a>
          </div>
        </div>

        <div className={styles.heroStage} aria-hidden>
          <article className={`${styles.heroSticky} ${styles.heroSticky1}`}>
            <div className={styles.stickyHeader}>Groceries</div>
            <div className={styles.stickyBody}>
              <p className={styles.stickyLine}>— sourdough</p>
              <p className={styles.stickyLine}>— olive oil</p>
              <p className={styles.stickyLine}>— flat-leaf parsley</p>
              <p className={styles.stickyLine}>— that small can of tomatoes</p>
            </div>
          </article>

          <article className={`${styles.heroSticky} ${styles.heroSticky2}`}>
            <div className={styles.stickyHeader}>Friday</div>
            <div className={styles.stickyBody}>
              <p className={styles.stickyLine}>Ship 0.2 release notes</p>
              <p className={styles.stickyLine}>Call mum 👋</p>
            </div>
            <div className={styles.stickyScheduleFoot}>
              Fri · 9:00 AM
            </div>
          </article>

          <article className={`${styles.heroSticky} ${styles.heroSticky3}`}>
            <div className={styles.stickyHeader}>Studio</div>
            <div className={styles.stickyBody}>
              <p className={styles.stickyLine}>The point isn't more apps.</p>
              <p className={styles.stickyLine}>The point is fewer, quieter</p>
              <p className={styles.stickyLine}>ones that get out of the way.</p>
            </div>
          </article>

          <article className={`${styles.heroSticky} ${styles.heroSticky4}`}>
            <div className={styles.stickyHeader}>Reminder</div>
            <div className={styles.stickyBody}>
              <p className={styles.stickyLine}>Stand up.</p>
              <p className={styles.stickyLine}>Drink water.</p>
            </div>
            <div className={styles.stickyScheduleFoot}>
              Every 2 hrs
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

/* ---------- Desktop layer explainer ---------- */

function DesktopLayer() {
  return (
    <section
      className={styles.desktopSection}
      aria-labelledby="desktop-layer-title"
    >
      <div className={`atlasContainer ${styles.desktopInner}`}>
        <div className={styles.desktopCopy}>
          <span className="atlasEyebrow">Desktop Layer</span>
          <h2
            id="desktop-layer-title"
            className={styles.sectionTitle}
          >
            Pinned to your desk.
            <br />
            <span className={styles.desktopTitleAccent}>
              Not stuck in another window.
            </span>
          </h2>
          <p className={styles.sectionLede}>
            Stickies don't open in a window of their own. They sit on
            the wallpaper, between the desktop and your shortcuts.
            Windows can cover them. Right-click the desktop to drop a
            new one anywhere.
          </p>
          <ul className={styles.desktopPoints}>
            <li>Click the title strip to name a note.</li>
            <li>Drag from the header strip. Snaps to a 16px grid.</li>
            <li>Resize from the corner. Same snap.</li>
            <li>Drag onto the trash icon to delete.</li>
          </ul>
        </div>

        <DesktopMockupAnimated />
      </div>
    </section>
  );
}

/* ---------- Animated desktop mockup ---------- */

/**
 * Single scene type for the desktop-layer demo. Each scene has a
 * `kind` discriminator + a duration. The driver advances scenes in
 * order, then loops at the end. Scene indices are referenced by
 * the SCENE_* constants below — keep those in sync if the array
 * order changes.
 */
type DeskScene =
  | { kind: "rest"; duration: number }
  | { kind: "hover-empty"; duration: number }
  | { kind: "create-y"; duration: number }
  | { kind: "move-to-body"; duration: number }
  | { kind: "click-body"; duration: number }
  | { kind: "type-body"; duration: number }
  | { kind: "rest-after-type"; duration: number }
  | { kind: "move-to-header"; duration: number }
  | { kind: "pickup-y"; duration: number }
  | { kind: "drag-y"; duration: number }
  | { kind: "drop-y"; duration: number }
  | { kind: "rest-after-drag"; duration: number }
  | { kind: "hover-resize-corner"; duration: number }
  | { kind: "resize-y"; duration: number }
  | { kind: "rest-after-resize"; duration: number }
  | { kind: "pickup-y-again"; duration: number }
  | { kind: "drag-y-to-trash"; duration: number }
  | { kind: "drop-on-trash"; duration: number }
  | { kind: "rest-loop-end"; duration: number };

const DESK_SCENES: DeskScene[] = [
  { kind: "rest", duration: 1500 },
  { kind: "hover-empty", duration: 600 },
  { kind: "create-y", duration: 500 },
  { kind: "move-to-body", duration: 500 },
  { kind: "click-body", duration: 350 },
  { kind: "type-body", duration: 2200 },
  { kind: "rest-after-type", duration: 600 },
  { kind: "move-to-header", duration: 500 },
  { kind: "pickup-y", duration: 350 },
  { kind: "drag-y", duration: 1100 },
  { kind: "drop-y", duration: 400 },
  { kind: "rest-after-drag", duration: 700 },
  // Resize sequence — cursor grabs the L-handle in Y's bottom-right
  // corner and drags it diagonally; Y grows to a larger size.
  { kind: "hover-resize-corner", duration: 500 },
  { kind: "resize-y", duration: 800 },
  { kind: "rest-after-resize", duration: 700 },
  { kind: "pickup-y-again", duration: 350 },
  { kind: "drag-y-to-trash", duration: 1300 },
  { kind: "drop-on-trash", duration: 500 },
  { kind: "rest-loop-end", duration: 1200 },
];

// Scene-index thresholds. The Y sticky exists from `create-y` through
// `drop-on-trash`; after that we're back to the initial state, ready
// to loop. Resize lands at scene 13 (`resize-y`) and the larger size
// persists through the trash sequence.
const SCENE_Y_CREATED = 2;
const SCENE_BODY_TYPED = 6;
const SCENE_Y_RESIZED = 13;
const SCENE_Y_TRASHED = 17;

// Y's positions during its life: where it's born (center of the
// desktop), where it ends up after the first drag, and where it
// converges before the trash shrink-out keyframe. Stored as %
// coordinates on the mockup so they scale with the container.
const Y_POS_ORIGIN = { x: 36, y: 36 };
const Y_POS_MOVED = { x: 38, y: 56 };
const Y_POS_TRASH = { x: 60, y: 58 };

// Sticky sizes — defined as % of the mockup. The default reads as
// square thanks to the 16:11 mockup aspect ratio (width × 16/11 ≈
// height in y-percent terms). Resized version is bigger and slightly
// taller-than-wide.
const Y_SIZE_DEFAULT = { width: 17, height: 25 };
const Y_SIZE_RESIZED = { width: 23, height: 34 };

const Y_BODY = "Take a walk.\nThen come back.";

// Cursor anchor positions inside the mockup as % of width/height.
// Tuned by eye against the 16:11 aspect ratio.
const DESK_ANCHORS = {
  rest: { x: 50, y: 50 },
  empty: { x: 40, y: 40 },           // Where Y will be created.
  yHeader: { x: 45, y: 39 },         // Y's header strip when at origin.
  yBody: { x: 45, y: 47 },           // Y's body when at origin.
  yHeaderMoved: { x: 47, y: 59 },    // Y's header after the drag.
  // Resize handle — the L-corner in the bottom-right of Y. Two
  // positions: default size and after the diagonal grow.
  yCornerDefault: { x: 55, y: 81 },  // (38 + 17, 56 + 25)
  yCornerResized: { x: 61, y: 90 },  // (38 + 23, 56 + 34)
  trash: { x: 93, y: 84 },           // The desktop trash icon center.
};

interface DeskState {
  showY: boolean;
  yBody: string;
  yTyping: boolean;
  yPos: { x: number; y: number };
  ySize: { width: number; height: number };
  yDragging: boolean;
  yTrashing: boolean;
  yAppearing: boolean;
  trashActive: boolean;
  cursorX: number;
  cursorY: number;
  clicking: boolean;
}

function deriveDesk(
  sceneIdx: number,
  scene: DeskScene,
  typed: number,
): DeskState {
  const showY = sceneIdx >= SCENE_Y_CREATED && sceneIdx <= SCENE_Y_TRASHED;
  const yAppearing = scene.kind === "create-y";
  // Size persists at the larger value from the resize scene onward
  // until the loop wraps. Y's CSS transition smooths the change.
  const ySize =
    sceneIdx >= SCENE_Y_RESIZED && sceneIdx <= SCENE_Y_TRASHED
      ? Y_SIZE_RESIZED
      : Y_SIZE_DEFAULT;

  let yPos = Y_POS_ORIGIN;
  let yDragging = false;
  let yTrashing = false;
  let trashActive = false;
  let cursorX = DESK_ANCHORS.rest.x;
  let cursorY = DESK_ANCHORS.rest.y;
  let clicking = false;

  switch (scene.kind) {
    case "rest":
    case "rest-loop-end":
      break;
    case "hover-empty":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.empty);
      break;
    case "create-y":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.empty);
      clicking = true;
      break;
    case "move-to-body":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yBody);
      break;
    case "click-body":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yBody);
      clicking = true;
      break;
    case "type-body":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yBody);
      break;
    case "rest-after-type":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yBody);
      break;
    case "move-to-header":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yHeader);
      break;
    case "pickup-y":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yHeader);
      yDragging = true;
      clicking = true;
      break;
    case "drag-y":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yHeaderMoved);
      yPos = Y_POS_MOVED;
      yDragging = true;
      break;
    case "drop-y":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yHeaderMoved);
      yPos = Y_POS_MOVED;
      break;
    case "rest-after-drag":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yHeaderMoved);
      yPos = Y_POS_MOVED;
      break;
    case "hover-resize-corner":
      // Cursor lands on Y's bottom-right L-handle while Y is still
      // at default size.
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yCornerDefault);
      yPos = Y_POS_MOVED;
      break;
    case "resize-y":
      // Cursor + Y's bottom-right corner drag outward diagonally.
      // The size flips to RESIZED via the sceneIdx threshold above;
      // CSS transitions on width/height handle the grow animation.
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yCornerResized);
      yPos = Y_POS_MOVED;
      yDragging = true;
      clicking = true;
      break;
    case "rest-after-resize":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yCornerResized);
      yPos = Y_POS_MOVED;
      break;
    case "pickup-y-again":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.yHeaderMoved);
      yPos = Y_POS_MOVED;
      yDragging = true;
      clicking = true;
      break;
    case "drag-y-to-trash":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.trash);
      yPos = Y_POS_TRASH;
      yDragging = true;
      trashActive = true;
      break;
    case "drop-on-trash":
      ({ x: cursorX, y: cursorY } = DESK_ANCHORS.trash);
      yPos = Y_POS_TRASH;
      trashActive = true;
      yTrashing = true;
      break;
  }

  // Body text grows char-by-char during type-body, then sits full
  // until the loop resets the Y entirely.
  const yBody =
    scene.kind === "type-body"
      ? Y_BODY.slice(0, typed)
      : sceneIdx >= SCENE_BODY_TYPED && showY
        ? Y_BODY
        : "";
  const yTyping = scene.kind === "type-body";

  return {
    showY,
    yBody,
    yTyping,
    yPos,
    ySize,
    yDragging,
    yTrashing,
    yAppearing,
    trashActive,
    cursorX,
    cursorY,
    clicking,
  };
}

function DesktopMockupAnimated() {
  const [sceneIdx, setSceneIdx] = useState(0);
  const scene = DESK_SCENES[sceneIdx]!;
  const [typed, setTyped] = useState(0);

  // Master scene timer — advances + wraps.
  useEffect(() => {
    setTyped(0);
    const id = window.setTimeout(() => {
      setSceneIdx((i) => (i + 1) % DESK_SCENES.length);
    }, scene.duration);
    return () => window.clearTimeout(id);
  }, [sceneIdx, scene.duration]);

  // Char-level typing tick — only fires during the type-body scene.
  useEffect(() => {
    if (scene.kind !== "type-body") return;
    const target = Y_BODY.length;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / (scene.duration - 200));
      setTyped(Math.round(t * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [sceneIdx, scene]);

  const state = useMemo(
    () => deriveDesk(sceneIdx, scene, typed),
    [sceneIdx, scene, typed],
  );

  return (
    <div className={styles.desktopMockup} aria-hidden>
      {/* ── Background stickies (these never change during the loop). ── */}
      <article className={`${styles.mockSticky} ${styles.mockStickyA}`}>
        <div className={styles.mockStickyMini}>Today</div>
        <div className={styles.mockStickyMiniBody}>
          Pick up the new keys
        </div>
      </article>
      <article className={`${styles.mockSticky} ${styles.mockStickyB}`}>
        <div className={styles.mockStickyMini}>This week</div>
        <div className={styles.mockStickyMiniBody}>
          Re-read the chapter
        </div>
      </article>
      <article className={`${styles.mockSticky} ${styles.mockStickyC}`}>
        <div className={styles.mockStickyMini}>Soon</div>
        <div className={styles.mockStickyMiniBody}>
          Email the studio back
        </div>
      </article>
      <article className={`${styles.mockSticky} ${styles.mockStickyD}`}>
        <div className={styles.mockStickyMini}>Idea</div>
        <div className={styles.mockStickyMiniBody}>
          A quieter dock — fewer pins, more breathing room.
        </div>
      </article>
      <article className={`${styles.mockSticky} ${styles.mockStickyE}`}>
        <div className={styles.mockStickyMini}>Note to self</div>
        <div className={styles.mockStickyMiniBody}>
          Stand up. Drink water.
        </div>
      </article>

      {/* ── Dynamic sticky Y — appears, gets edited, dragged, trashed. ── */}
      {state.showY ? (
        <article
          key={`y-${sceneIdx < SCENE_Y_CREATED ? 0 : 1}`}
          className={[
            styles.mockStickyDynamic,
            state.yAppearing ? styles.mockStickyAppear : "",
            state.yDragging ? styles.mockStickyDragging : "",
            state.yTrashing ? styles.mockStickyTrashing : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            top: `${state.yPos.y}%`,
            left: `${state.yPos.x}%`,
            width: `${state.ySize.width}%`,
            height: `${state.ySize.height}%`,
          }}
        >
          <div className={styles.mockStickyMini}>Today</div>
          <div className={styles.mockStickyMiniBody}>
            {state.yBody}
            {state.yTyping ? (
              <span className={styles.mockCaret} aria-hidden />
            ) : null}
          </div>
        </article>
      ) : null}

      {/* ── Desktop trash target. Highlights when Y is being dragged
            over it. ── */}
      <div
        className={
          state.trashActive
            ? `${styles.mockTrash} ${styles.mockTrashActive}`
            : styles.mockTrash
        }
      >
        <TrashIcon />
      </div>

      {/* ── Taskbar — clock only. ── */}
      <div className={styles.mockTaskbar}>
        <span className={styles.mockTaskbarClock}>2:14 PM</span>
      </div>

      {/* ── Animated cursor. ── */}
      <DeskCursor
        x={state.cursorX}
        y={state.cursorY}
        clicking={state.clicking}
      />
    </div>
  );
}

function DeskCursor({
  x,
  y,
  clicking,
}: {
  x: number;
  y: number;
  clicking: boolean;
}) {
  return (
    <div
      className={
        clicking
          ? `${styles.mockCursor} ${styles.mockCursorClick}`
          : styles.mockCursor
      }
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      <svg viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3 2 L3 18 L7.5 13.5 L10.5 19.5 L13 18.2 L10 12.5 L16 12 Z"
          fill="#fff8e5"
          stroke="#171717"
          strokeWidth="1.6"
          strokeLinejoin="miter"
        />
      </svg>
      <span className={styles.mockCursorRipple} aria-hidden />
    </div>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#171717"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 7 L20 7" />
      <path d="M9 7 V5 a1 1 0 0 1 1 -1 h4 a1 1 0 0 1 1 1 V7" />
      <path d="M6 7 L7.2 20 a1.5 1.5 0 0 0 1.5 1.4 h6.6 a1.5 1.5 0 0 0 1.5 -1.4 L18 7" />
      <line x1="10" y1="11" x2="10" y2="18" />
      <line x1="14" y1="11" x2="14" y2="18" />
    </svg>
  );
}

/* ---------- Color palette ---------- */

const SWATCHES: { label: string; quote: string; className: string }[] = [
  { label: "Yellow",     quote: "The default. The classic.",       className: "colorYellow" },
  { label: "Vermillion", quote: "Loud. Use sparingly.",            className: "colorVermillion" },
  { label: "Teal",       quote: "Cool. For long thoughts.",        className: "colorTeal" },
  { label: "Green",      quote: "Calm. Things to remember.",       className: "colorGreen" },
  { label: "Mustard",    quote: "Warm. Today's intentions.",       className: "colorMustard" },
  { label: "Cream",      quote: "Quiet. Almost a whisper.",        className: "colorCream" },
  { label: "Lavender",   quote: "Soft. End-of-day reflections.",   className: "colorLavender" },
];

function Palette() {
  return (
    <section
      className={styles.paletteSection}
      aria-labelledby="palette-title"
    >
      <div className="atlasContainer">
        <header className={styles.paletteHeader}>
          <div>
            <span className="atlasEyebrow">Seven colors</span>
            <h2 id="palette-title" className={styles.sectionTitle}>
              Choose by mood,{" "}
              <span className={styles.paletteTitleAccent}>
                not by category.
              </span>
            </h2>
            <p
              className={styles.sectionLede}
              style={{ marginTop: "var(--sp-3)" }}
            >
              Right-click a sticky to swap colors. No tags, no folders,
              no taxonomy — just a palette that helps you tell one
              note from another at a glance.
            </p>
          </div>
        </header>

        <div className={styles.paletteGrid}>
          {SWATCHES.map((s) => (
            <article
              key={s.label}
              className={`${styles.swatch} ${styles[s.className]!}`}
              aria-label={s.label}
            >
              <div className={styles.swatchHeader}>{s.label}</div>
              <div className={styles.swatchBody}>{s.quote}</div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Scheduled reminders ---------- */

function Schedule() {
  return (
    <section
      className={styles.scheduleSection}
      aria-labelledby="schedule-title"
    >
      <div className={`atlasContainer ${styles.scheduleInner}`}>
        <div>
          <span className="atlasEyebrow">Reminders</span>
          <h2
            id="schedule-title"
            className={styles.sectionTitle}
            style={{ marginTop: "var(--sp-3)" }}
          >
            Drop a note.{" "}
            <span className={styles.scheduleTitleAccent}>
              Walk away.
            </span>
            <br />
            It'll come back when you need it.
          </h2>
          <p
            className={styles.sectionLede}
            style={{ marginTop: "var(--sp-4)" }}
          >
            Attach a schedule to any sticky — once on a future date,
            weekly on the days you pick, or monthly by date or weekday.
            Trash a scheduled note and it doesn't really go away. It
            sleeps. Then it shows up again, right where you left it,
            the next time it's due.
          </p>
          <p
            className={styles.sectionLede}
            style={{ marginTop: "var(--sp-3)", color: "var(--muted)" }}
          >
            No accounts. No push servers. The clock that wakes them up
            is your own.
          </p>
        </div>

        <div className={styles.scheduleStage} aria-hidden>
          <article className={styles.scheduleSticky}>
            <div className={styles.scheduleStickyHeader}>Tuesdays</div>
            <div className={styles.scheduleStickyBody}>
              Water the plants.
              <br />
              All of them — even the one by the window.
            </div>
            <div className={styles.scheduleStickyFooter}>
              Weekly · Tue · 8:00 AM
            </div>
          </article>

          <div className={styles.scheduleDialog}>
            <div className={styles.scheduleDialogBar}>
              Schedule this sticky
            </div>
            <div className={styles.scheduleDialogBody}>
              <div className={styles.scheduleOption}>
                <span className={styles.scheduleRadio} />
                Once
              </div>
              <div
                className={`${styles.scheduleOption} ${styles.scheduleOptionActive}`}
              >
                <span className={styles.scheduleRadio} />
                Weekly
              </div>
              <div className={styles.scheduleOption}>
                <span className={styles.scheduleRadio} />
                Monthly · by date
              </div>
              <div className={styles.scheduleOption}>
                <span className={styles.scheduleRadio} />
                Monthly · by weekday
              </div>

              <div className={styles.scheduleFieldRow}>
                <span className={styles.scheduleField}>Tue</span>
                <span className={styles.scheduleField}>08:00</span>
              </div>

              <button type="button" className={styles.scheduleConfirm}>
                Set reminder
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Feature ribbon ---------- */

const FEATURES = [
  {
    stamp: "Drag",
    title: "Drag anywhere",
    body: "Grab the header strip and toss the note across your desk. Snaps to a tight 16px grid so things stay tidy.",
  },
  {
    stamp: "Resize",
    title: "Resize the corner",
    body: "Pinch the L-handle to make a note the size of the thought. Minimum 144 × 112, no maximum.",
  },
  {
    stamp: "Titles",
    title: "Click-to-edit titles",
    body: "Tap the title strip and type. Implicit titles follow the note's color; once you write one yourself, it sticks.",
  },
  {
    stamp: "Trash",
    title: "Drop onto the bin",
    body: "Drag a sticky onto the desktop trash icon to remove it. Scheduled notes don't disappear — they archive.",
  },
  {
    stamp: "Pin",
    title: "Pin from the tray",
    body: "Hide every sticky with a single click in the taskbar. Click again and they all come back, in place.",
  },
  {
    stamp: "Offline",
    title: "Offline. Always.",
    body: "Local IndexedDB, no sync, no telemetry. Your stickies are on this machine, period.",
  },
];

function Features() {
  return (
    <section
      className={styles.featureSection}
      aria-labelledby="features-title"
    >
      <div className={`atlasContainer ${styles.sectionInner}`}>
        <header className={styles.sectionHeader}>
          <span className="atlasEyebrow">What it does</span>
          <h2
            id="features-title"
            className={`${styles.sectionTitle} ${styles.featureTitle}`}
          >
            Small surface.{" "}
            <span className={styles.featureTitleAccent}>
              Just enough behaviour.
            </span>
          </h2>
        </header>

        <div className={styles.featureGrid}>
          {FEATURES.map((f) => (
            <article key={f.title} className={styles.feature}>
              <span className={styles.featureStamp}>{f.stamp}</span>
              <h3 className={styles.featureName}>{f.title}</h3>
              <p className={styles.featureBody}>{f.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- CTA ---------- */

function Cta() {
  return (
    <section className={styles.ctaSection} aria-labelledby="cta-title">
      <div className={styles.ctaConfetti} aria-hidden>
        <span className={styles.ctaConfettiOne} />
        <span className={styles.ctaConfettiTwo} />
        <span className={styles.ctaConfettiThree} />
        <span className={styles.ctaConfettiFour} />
      </div>

      <div className={`atlasContainer ${styles.ctaInner}`}>
        <h2 id="cta-title" className={styles.ctaTitle}>
          Comes free with{" "}
          <span className={styles.ctaTitleAccent}>Atlas Desk.</span>
        </h2>
        <p className={styles.ctaLede}>
          Stickies ships with the suite — alongside Notes, Music,
          Layouts, and the rest. One install, no add-ons, no upsell.
        </p>
        <div className={styles.ctaActions}>
          <a className={styles.heroPrimary} href="/#download">
            Download Atlas Desk <span aria-hidden>→</span>
          </a>
          <a className={styles.heroSecondary} href="/#apps">
            See every app
          </a>
        </div>
      </div>
    </section>
  );
}
